import React, { useEffect, useState } from "react";
import { PlusCircle, Download, RotateCcw, Trash2, Coins } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatINR, downloadBlob } from "@/lib/api";
import { toast } from "sonner";
import { KARAT_FACTORS, fmtGrams } from "@/pages/admin/orders/orderConstants";

const CN_STATUS_LABELS = { active: "Active", partially_redeemed: "Partially Redeemed", redeemed: "Redeemed", settled: "Settled" };
const CN_STATUS_STYLE = {
  active: "bg-[hsl(var(--gold-light))] text-[hsl(var(--gold-dark))] border border-border",
  partially_redeemed: "bg-[#F7F0E6] text-[#8A6A1F] border border-border",
  redeemed: "bg-muted text-muted-foreground border border-border",
  settled: "bg-[#E7F3EA] text-[#1F5B2E] border border-border",
};
const BUYBACK_CATEGORIES = ["Old Gold Buy-back", "Scrap Purchase", "Other"];

export const CreditNotesTab = ({ staff }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [orderQuery, setOrderQuery] = useState("");
  const [orderMatches, setOrderMatches] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [returnQty, setReturnQty] = useState({});
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [deleteFor, setDeleteFor] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleting, setDeleting] = useState(false);

  // Manual Credit Note (Old Gold Buy-back / Scrap Purchase)
  const [manualOpen, setManualOpen] = useState(false);
  const [shops, setShops] = useState([]);
  const [shopQuery, setShopQuery] = useState("");
  const [shopMatches, setShopMatches] = useState([]);
  const [manualShop, setManualShop] = useState(null);
  const [manualCategory, setManualCategory] = useState("Old Gold Buy-back");
  const [manualWeight, setManualWeight] = useState("");
  const [manualPurity, setManualPurity] = useState("24K");
  const [manualFinePct, setManualFinePct] = useState("");
  const [manualInr, setManualInr] = useState("");
  const [manualNote, setManualNote] = useState("");
  const [manualSubmitting, setManualSubmitting] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/crm/credit-notes").then(({ data }) => setNotes(data)).catch(() => setNotes([])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const searchOrders = async (q) => {
    setOrderQuery(q);
    if (!q.trim()) { setOrderMatches([]); return; }
    try {
      const { data } = await api.get("/crm/orders", { params: { search: q } });
      setOrderMatches(data.filter((o) => !["cancelled", "rejected"].includes(o.status)).slice(0, 8));
    } catch { setOrderMatches([]); }
  };
  const pickOrder = (o) => {
    setSelectedOrder(o);
    setOrderMatches([]);
    setOrderQuery(`${o.order_no} — ${o.shop_name}`);
    const initial = {};
    (o.items || []).forEach((it) => { initial[it.product_id] = 0; });
    setReturnQty(initial);
  };
  const openNew = () => {
    setOpen(true); setSelectedOrder(null); setOrderQuery(""); setOrderMatches([]); setReturnQty({}); setReason("");
  };
  const submit = async () => {
    if (!selectedOrder) { toast.error("Select an order first"); return; }
    const items = Object.entries(returnQty).filter(([, q]) => Number(q) > 0).map(([pid, q]) => ({ product_id: pid, quantity: Number(q) }));
    if (items.length === 0) { toast.error("Enter a return quantity for at least one item"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/crm/orders/${selectedOrder.id}/credit-notes`, { items, reason });
      toast.success(`Credit note ${data.credit_note.credit_note_no} created — available as a voucher on Record Payment`);
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create credit note"); }
    finally { setSubmitting(false); }
  };
  const downloadPdf = (cn) => downloadBlob(`/crm/credit-notes/${cn.id}/pdf`, `CreditNote-${cn.credit_note_no}.pdf`).catch(() => toast.error("Download failed"));

  const openDelete = (cn) => { setDeleteFor(cn); setDeleteReason(""); };
  const submitDelete = async () => {
    if (!deleteReason.trim()) { toast.error("A reason is required to delete a credit note"); return; }
    setDeleting(true);
    try {
      await api.delete(`/crm/credit-notes/${deleteFor.id}`, { params: { reason: deleteReason.trim() } });
      toast.success(`Credit note ${deleteFor.credit_note_no} deleted — ledger reversed`);
      setDeleteFor(null); setDeleteReason(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not delete credit note"); }
    finally { setDeleting(false); }
  };

  // ---- Manual buy-back credit note ----
  const openManual = () => {
    setManualOpen(true); setManualShop(null); setShopQuery(""); setShopMatches([]);
    setManualCategory("Old Gold Buy-back"); setManualWeight(""); setManualPurity("24K");
    setManualFinePct(""); setManualInr(""); setManualNote("");
    if (shops.length === 0) {
      api.get("/crm/shops", { params: { status: "approved" } }).then(({ data }) => setShops(data)).catch(() => setShops([]));
    }
  };
  const searchShops = (q) => {
    setShopQuery(q); setManualShop(null);
    if (!q.trim()) { setShopMatches([]); return; }
    const ql = q.toLowerCase();
    setShopMatches(shops.filter((s) => (s.shop_name || "").toLowerCase().includes(ql) || (s.phone || "").includes(q)).slice(0, 8));
  };
  const pickShop = (s) => { setManualShop(s); setShopQuery(`${s.shop_name} — ${s.phone}`); setShopMatches([]); };
  const manualFinePreview = (() => {
    const w = Number(manualWeight) || 0;
    if (w <= 0) return 0;
    const factor = manualPurity === "Other" ? (Number(manualFinePct) || 0) / 1000 : (KARAT_FACTORS[manualPurity] ?? 1);
    return Math.round(w * factor * 1000) / 1000;
  })();
  const submitManual = async () => {
    if (!manualShop) { toast.error("Select a partner"); return; }
    if (!(Number(manualWeight) > 0) && !(Number(manualInr) > 0)) { toast.error("Enter a Gold weight and/or a Rupee amount"); return; }
    setManualSubmitting(true);
    try {
      const { data } = await api.post("/crm/credit-notes/manual", {
        shop_id: manualShop.id, category: manualCategory,
        gold_weight_g: Number(manualWeight) || 0, gold_purity: manualPurity,
        gold_purity_fine_pct: manualPurity === "Other" ? Number(manualFinePct) || 0 : 0,
        inr_amount: Number(manualInr) || 0, note: manualNote,
      });
      toast.success(`Credit Note ${data.credit_note.credit_note_no} created — partner's ledger credited instantly`);
      setManualOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create the manual credit note"); }
    finally { setManualSubmitting(false); }
  };

  return (
    <div data-testid="credit-notes-tab">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <p className="text-sm text-muted-foreground max-w-xl">Product returns issue a redeemable voucher Credit Note. Old Gold Buy-back / Scrap Purchase credit the partner's ledger instantly.</p>
        <div className="flex items-center gap-2">
          {staff?.is_manager && (
            <Button data-testid="new-manual-credit-note-button" size="sm" variant="outline" className="gap-1.5" onClick={openManual}><Coins className="h-4 w-4" /> Create Manual Credit Note</Button>
          )}
          <Button data-testid="new-credit-note-button" size="sm" className="gap-1.5" onClick={openNew}><PlusCircle className="h-4 w-4" /> New Credit Note</Button>
        </div>
      </div>
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table data-testid="credit-notes-table">
            <TableHeader><TableRow className="bg-[#F4EBDD]">
              <TableHead>Credit Note</TableHead><TableHead>Invoice / Type</TableHead><TableHead>Shop</TableHead>
              <TableHead>Date</TableHead><TableHead>Reason</TableHead>
              <TableHead className="text-right">Gold Credited</TableHead>
              <TableHead className="text-right">Original</TableHead>
              <TableHead className="text-right">Remaining</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={10} className="text-center py-10 text-muted-foreground">Loading...</TableCell></TableRow>
              ) : notes.length === 0 ? (
                <TableRow><TableCell colSpan={10} className="text-center py-10 text-muted-foreground">No credit notes yet.</TableCell></TableRow>
              ) : notes.map((cn) => (
                <TableRow key={cn.id} className="hover:bg-[#F7F0E6]" data-testid="credit-note-row">
                  <TableCell className="font-mono text-sm">{cn.credit_note_no}</TableCell>
                  <TableCell className="text-sm">
                    {cn.source === "manual"
                      ? <Badge variant="outline" className="text-xs font-normal">Manual · {cn.category}</Badge>
                      : <span className="font-mono">{cn.invoice_no || cn.order_no}</span>}
                  </TableCell>
                  <TableCell className="text-sm">{cn.shop_name}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{(cn.created_at || "").slice(0, 10)}</TableCell>
                  <TableCell className="text-xs max-w-[160px] truncate">{cn.reason || "-"}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{cn.source === "manual" ? fmtGrams(cn.gold_fine_g) : "-"}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{formatINR(cn.original_amount)}</TableCell>
                  <TableCell className="text-right font-mono text-sm font-medium">{formatINR(cn.remaining_amount)}</TableCell>
                  <TableCell>
                    <Badge data-testid="credit-note-status-badge" className={`${CN_STATUS_STYLE[cn.status] || ""} text-xs`}>{CN_STATUS_LABELS[cn.status] || cn.status}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center gap-1.5 justify-end">
                      <Button data-testid="credit-note-download-pdf" size="sm" variant="outline" className="gap-1" onClick={() => downloadPdf(cn)}><Download className="h-3.5 w-3.5" /></Button>
                      {staff?.is_manager && (
                        <Button data-testid="credit-note-delete-button" size="sm" variant="outline" className="gap-1 text-destructive hover:text-destructive" onClick={() => openDelete(cn)}><Trash2 className="h-3.5 w-3.5" /></Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="new-credit-note-dialog" className="max-w-2xl">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><RotateCcw className="h-4 w-4" /> New Credit Note / Return</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Order / Invoice</Label>
              <Input data-testid="credit-note-order-search" value={orderQuery} onChange={(e) => searchOrders(e.target.value)}
                placeholder="Search by order no, invoice no, or shop name" className="mt-1" disabled={!!selectedOrder} />
              {selectedOrder && <Button size="sm" variant="ghost" className="mt-1 h-6 text-xs" onClick={openNew}>Change order</Button>}
              {orderMatches.length > 0 && (
                <div className="mt-1 border border-border rounded-lg max-h-40 overflow-y-auto">
                  {orderMatches.map((o) => (
                    <button key={o.id} type="button" data-testid="credit-note-order-result" onClick={() => pickOrder(o)}
                      className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-[#F7F0E6] text-left transition-colors duration-150">
                      <span>{o.invoice_no || o.order_no} — {o.shop_name}</span>
                      <span className="font-mono">{formatINR(o.total)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {selectedOrder && (
              <div className="border border-border rounded-lg divide-y divide-border" data-testid="credit-note-items-list">
                {(selectedOrder.items || []).map((it) => (
                  <div key={it.product_id} className="flex items-center justify-between px-3 py-2 text-sm gap-2">
                    <div className="flex-1 min-w-0"><div className="truncate">{it.name}</div><div className="text-xs text-muted-foreground">Ordered qty: {it.quantity} · {formatINR(it.unit_price)}/unit</div></div>
                    <Input data-testid="credit-note-item-qty" type="number" min={0} max={it.quantity} value={returnQty[it.product_id] || 0}
                      onChange={(e) => setReturnQty((prev) => ({ ...prev, [it.product_id]: Math.min(Number(e.target.value) || 0, it.quantity) }))}
                      className="w-20 h-8 text-center" />
                  </div>
                ))}
                <div>
                  <Label className="text-xs px-3 pt-2 block">Reason for return</Label>
                  <Textarea data-testid="credit-note-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="border-0 rounded-none focus-visible:ring-0" placeholder="e.g. Customer returned due to sizing issue" />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="credit-note-submit" disabled={submitting || !selectedOrder} onClick={submit}>{submitting ? "Creating..." : "Create Credit Note"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteFor} onOpenChange={(o) => !o && setDeleteFor(null)}>
        <DialogContent data-testid="delete-credit-note-dialog" className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Credit Note {deleteFor?.credit_note_no}?</DialogTitle>
            <DialogDescription>
              This reverses any credit already applied and posts an equal correction to the partner's ledger. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs">Reason (required)</Label>
            <Textarea data-testid="delete-credit-note-reason-input" value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)} rows={2} className="mt-1" placeholder="Why is this credit note being deleted?" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteFor(null)}>Cancel</Button>
            <Button data-testid="delete-credit-note-confirm" variant="destructive" disabled={deleting || !deleteReason.trim()} onClick={submitDelete}>{deleting ? "Deleting..." : "Delete & Reverse"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Credit Note — Old Gold Buy-back / Scrap Purchase (instant ledger credit) */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent data-testid="manual-credit-note-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Coins className="h-4 w-4" /> Create Manual Credit Note</DialogTitle>
            <DialogDescription>For Old Gold Buy-back or Scrap Purchase — instantly credits the partner's Gold and/or Rupee ledger. No invoice reference needed.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Partner</Label>
              <Input data-testid="manual-cn-shop-search" value={shopQuery} onChange={(e) => searchShops(e.target.value)}
                placeholder="Search by shop name or phone" className="mt-1" disabled={!!manualShop} />
              {manualShop && <Button size="sm" variant="ghost" className="mt-1 h-6 text-xs" onClick={() => { setManualShop(null); setShopQuery(""); }}>Change partner</Button>}
              {shopMatches.length > 0 && (
                <div className="mt-1 border border-border rounded-lg max-h-40 overflow-y-auto">
                  {shopMatches.map((s) => (
                    <button key={s.id} type="button" data-testid="manual-cn-shop-result" onClick={() => pickShop(s)}
                      className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-[#F7F0E6] text-left transition-colors duration-150">
                      <span>{s.shop_name}</span><span className="text-muted-foreground font-mono">{s.phone}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs">Category</Label>
              <Select value={manualCategory} onValueChange={setManualCategory}>
                <SelectTrigger data-testid="manual-cn-category-select" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{BUYBACK_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Gold Weight (g)</Label>
                <Input data-testid="manual-cn-weight-input" type="number" value={manualWeight} onChange={(e) => setManualWeight(e.target.value)} className="mt-1 font-mono" placeholder="0.000" />
              </div>
              <div><Label className="text-xs">Purity</Label>
                <Select value={manualPurity} onValueChange={setManualPurity}>
                  <SelectTrigger data-testid="manual-cn-purity-select" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="24K">24K (999)</SelectItem>
                    <SelectItem value="22K">22K (916)</SelectItem>
                    <SelectItem value="18K">18K (750)</SelectItem>
                    <SelectItem value="14K">14K (585)</SelectItem>
                    <SelectItem value="9K">9K (375)</SelectItem>
                    <SelectItem value="Other">Other (per-mille)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {manualPurity === "Other" && (
                <div className="col-span-2"><Label className="text-xs">Purity (per-mille)</Label>
                  <Input data-testid="manual-cn-finepct-input" type="number" value={manualFinePct} onChange={(e) => setManualFinePct(e.target.value)} className="mt-1 font-mono" placeholder="e.g. 916" />
                </div>
              )}
            </div>
            {Number(manualWeight) > 0 && (
              <div className="rounded-lg bg-[hsl(var(--gold-light))] p-2.5 text-sm">
                Credits fine gold: <span data-testid="manual-cn-fine-preview" className="font-mono font-semibold text-[hsl(var(--gold-dark))]">{fmtGrams(manualFinePreview)}</span>
                <span className="text-muted-foreground"> (24K equivalent)</span>
              </div>
            )}
            <div><Label className="text-xs">Rupee Amount (optional)</Label>
              <Input data-testid="manual-cn-inr-input" type="number" value={manualInr} onChange={(e) => setManualInr(e.target.value)} className="mt-1 font-mono" placeholder="0" />
            </div>
            <div><Label className="text-xs">Note (optional)</Label>
              <Textarea data-testid="manual-cn-note-input" value={manualNote} onChange={(e) => setManualNote(e.target.value)} rows={2} placeholder="e.g. Old bangle exchange, 22K, weighed in-store" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualOpen(false)}>Cancel</Button>
            <Button data-testid="manual-cn-submit" disabled={manualSubmitting || !manualShop} onClick={submitManual}>
              {manualSubmitting ? "Creating..." : "Create & Credit Ledger"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
